/**
 * FilterOverlay Component
 * 
 * This React component renders a filtering overlay using Material-UI (MUI) components.
 * The overlay allows users to filter stations based on selected State and Year.
 * It displays a title based on the active tab and provides dropdown menus for selecting
 * a state and a year, along with a Filter button to apply the selections.
 * 
 * Props:
 * - selectedState: currently selected state value.
 * - setSelectedState: function to update the selected state.
 * - selectedYear: currently selected year value.
 * - setSelectedYear: function to update the selected year.
 * - selectedSeason: currently selected season value.
 * - setSelectedSeason: function to update the selected season.
 * - handleFilter: callback function invoked when the Filter button is clicked.
 * - states: array of state options for the dropdown.
 * - years: array of year options for the dropdown.
 * - activeTab: string representing the currently active tab, used in the title.
 * - showTrawlShapefile: boolean indicating whether to show the trawl shapefile.
 * - setShowTrawlShapefile: function to update the showTrawlShapefile state.
 * - showHistoricTrawlShapefile: boolean indicating whether to show the historic trawl shapefile.
 * - setShowHistoricTrawlShapefile: function to update the showHistoricTrawlShapefile state.
 * - showLongline2008Shapefile: boolean indicating whether to show the 2008 Longline shapefile.
 * - setShowLongline2008Shapefile: function to update the showLongline2008Shapefile state.
 * - showLongline2015Shapefile: boolean indicating whether to show the 2015 Longline shapefile.
 * - setShowLongline2015Shapefile: function to update the showLongline2015Shapefile state.
 * - showLongline2024Shapefile: boolean indicating whether to show the 2024 Longline shapefile.
 * - setShowLongline2024Shapefile: function to update the showLongline2024Shapefile state.
 * - isCollapsed: boolean indicating whether the component is collapsed.
 * - setIsCollapsed: function to update the isCollapsed state.
 * 
 * The component is positioned absolutely near the top-left corner of the screen, 
 * styled as a Paper with padding, margin, and shadow for a clean overlay appearance.
 */

import React from 'react'
import { Typography, FormControl, Select, MenuItem, Button, Paper, FormControlLabel, Switch, Autocomplete, TextField, Chip, Box } from '@mui/material'
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import { commonSwitchStyles } from './SwitchStyles'
import DateRangeFields from './DateRangeFields'

const FILTER_OVERLAY_Z_INDEX = 1700

const renderMutedPlaceholder = (label) => (
  <Box component="span" sx={{ color: 'rgba(255, 245, 230, 0.52)', fontStyle: 'normal', fontWeight: 400 }}>
    {label}
  </Box>
)

function FilterOverlay({ 
  selectedState, setSelectedState, 
  selectedYear, setSelectedYear, 
  selectedSeason, setSelectedSeason,
  handleFilter, states, years, activeTab,
  showTrawlShapefile, setShowTrawlShapefile,
  showHistoricTrawlShapefile, setShowHistoricTrawlShapefile,
  showLongline2008Shapefile, setShowLongline2008Shapefile,
  showLongline2015Shapefile, setShowLongline2015Shapefile,
  showLongline2024Shapefile, setShowLongline2024Shapefile,
  isCollapsed, setIsCollapsed,
  dateRangeStart, setDateRangeStart,
  dateRangeEnd, setDateRangeEnd
}) {
  // Determine the title based on the active tab
  const getTitle = () => {
    if (activeTab === 'TrawlStations') return 'Trawl Stations';
    if (activeTab === 'LonglineStations') return 'Longline Stations';
    if (activeTab === 'PlanktonStations') return 'Plankton Stations';
    return activeTab;
  };

  // Check if this is a trawl tab
  const isTrawlStationsTab = activeTab === 'TrawlStations';
  
  // Check if this is the Longline Stations tab specifically
  const isLonglineStationsTab = activeTab === 'LonglineStations';
  const isPlanktonStationsTab = activeTab === 'PlanktonStations';
  
  // Check if this is any stations tab (either Trawl or Longline)
  const isStationsTab = isTrawlStationsTab || isLonglineStationsTab || isPlanktonStationsTab;

  // State for controlling dropdown open/close
  const [stateDropdownOpen, setStateDropdownOpen] = React.useState(false);
  const [yearDropdownOpen, setYearDropdownOpen] = React.useState(false);

  // Reset dropdown states when activeTab changes
  React.useEffect(() => {
    setStateDropdownOpen(false);
    setYearDropdownOpen(false);
  }, [activeTab]);

  // If collapsed, show minimal bar
  if (isCollapsed) {
    return (
      <Paper 
        elevation={0} 
        sx={{
          position: 'fixed',
          top: 116, // 100px header height + 16px spacing
          left: 16,
          zIndex: FILTER_OVERLAY_Z_INDEX,
          background: 'rgba(45, 52, 54, 0.95)', // Solid warm charcoal background
          padding: '12px 18px',
          width: '260px',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(32, 178, 170, 0.08)', // Subtle Light Sea Green border
          borderRadius: '4px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          userSelect: 'none',
          '&:hover': {
            background: 'rgba(45, 52, 54, 1)', // Darker on hover
          }
        }}
        onClick={() => setIsCollapsed(false)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography 
            sx={{ 
              fontFamily: "'Nunito', 'Roboto', sans-serif",
              fontSize: '15px',
              fontWeight: 600,
              color: '#20B2AA', // Light Sea Green text
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            {getTitle()}
          </Typography>
          <Box sx={{
            color: '#20B2AA', // Light Sea Green arrow
            display: 'flex',
            alignItems: 'center',
            transition: 'transform 0.2s ease-in-out'
          }}>
            <KeyboardArrowDown sx={{ fontSize: '20px' }} />
          </Box>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper 
      elevation={0} 
      sx={{
        position: 'fixed',
        top: 116, // 100px header height + 16px spacing
        left: 16,
        zIndex: FILTER_OVERLAY_Z_INDEX,
        background: 'rgba(45, 52, 54, 0.95)', // Solid warm charcoal background
        padding: '12px 18px',
        width: '260px',
        backdropFilter: 'blur(8px)',
        border: '1px solid rgba(32, 178, 170, 0.08)', // Subtle Light Sea Green border
        borderRadius: '4px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        userSelect: 'none'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, mt: 0 }}>
        <Typography
          sx={{
            fontFamily: "'Nunito', 'Roboto', sans-serif",
            fontSize: '15px',
            fontWeight: 600,
            color: '#20B2AA', // Light Sea Green text
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            display: 'inline-block'
          }}
        >
          {getTitle()}
        </Typography>
        <Box
          sx={{
            color: '#20B2AA', // Light Sea Green chevron
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            transition: 'transform 0.2s ease-in-out',
            '&:hover': {
              transform: 'scale(1.1)'
            }
          }}
          onClick={() => setIsCollapsed(true)}
        >
          <KeyboardArrowUp sx={{ fontSize: '20px' }} />
        </Box>
      </Box>

      {isStationsTab ? (
        <FormControl fullWidth size='small' sx={{ mb: 2 }}>
          <Autocomplete
            multiple
            id="state-multi-select"
            options={['--Select--', 'ALL SOURCES', ...states]}
            value={Array.isArray(selectedState) ? selectedState : (selectedState ? [selectedState] : [])}
            open={stateDropdownOpen}
            onOpen={() => setStateDropdownOpen(true)}
            onClose={() => setStateDropdownOpen(false)}
            popupIcon={
              <KeyboardArrowDown
                onClick={(e) => {
                  e.stopPropagation();
                  setStateDropdownOpen(!stateDropdownOpen);
                }}
                sx={{ cursor: 'pointer', fontSize: '20px' }}
              />
            }
            disableCloseOnSelect={false}
            blurOnSelect="mouse"
            onChange={(e, newValue) => {
              // If selecting "--Select--", ignore it
              if (newValue.includes('--Select--')) {
                const filteredValue = newValue.filter(val => val !== '--Select--');
                setSelectedState(filteredValue);
                return;
              }
              
              // If selecting "ALL SOURCES", clear other selections
              if (newValue.includes('ALL SOURCES') && newValue.length > 1) {
                setSelectedState(['ALL SOURCES']);
              } else if (newValue.includes('ALL SOURCES') && newValue.length === 1) {
                setSelectedState(newValue);
              } else {
                // If selecting a state and "ALL SOURCES" is already selected, remove "ALL SOURCES"
                const filteredValue = newValue.filter(val => val !== 'ALL SOURCES');
                setSelectedState(filteredValue);
              }
            }}
            renderInput={(params) => (
              <TextField 
                {...params} 
                placeholder={selectedState?.length ? '' : 'Source'}
                size="small" 
                inputProps={{
                  ...params.inputProps,
                  'aria-label': 'Source'
                }}
                sx={{
                  fontSize: '13px',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.07)',
                    '& fieldset': {
                      borderColor: 'rgba(184, 216, 208, 0.3)',
                      borderWidth: '1px',
                    },
                    '&:hover fieldset': {
                      borderColor: '#20B2AA',
                      borderWidth: '1.5px',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#20B2AA',
                      borderWidth: '1.5px',
                      boxShadow: '0 0 0 2px rgba(184, 216, 208, 0.2)',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '13px',
                    color: '#FFF5E6', // Warm cream labels
                  },
                  '& .MuiInputBase-input': {
                    color: '#FFF5E6', // Warm cream text
                  }
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option}
                  {...getTagProps({ index })}
                  size="small"
                  sx={{
                    backgroundColor: option === 'ALL SOURCES' ? 'rgba(46, 90, 136, 0.15)' : 'rgba(46, 90, 136, 0.08)',
                    fontSize: '13px',
                    fontWeight: option === 'ALL SOURCES' ? 600 : 400,
                    height: '24px'
                  }}
                />
              ))
            }
            renderOption={(props, option) => (
              <li {...props} style={{ 
                fontWeight: option === 'ALL SOURCES' ? 600 : 400,
                fontSize: '13px'
              }}>
                {option === '--Select--' ? renderMutedPlaceholder('--Select--') : option}
              </li>
            )}
            size='small'
            sx={{
              fontSize: '13px',
              '& .MuiAutocomplete-inputRoot': {
                minHeight: '36px'
              },
              '& .MuiAutocomplete-input': {
                fontSize: '13px',
                height: '1.4375em'
              },
              '& .MuiAutocomplete-endAdornment': {
                '& .MuiSvgIcon-root': {
                  color: '#20B2AA'
                }
              }
            }}
            ListboxProps={{
              style: {
                fontSize: '13px'
              }
            }}
            PaperProps={{
              sx: {
                '&.MuiPaper-root': {
                  backgroundColor: '#2D3436 !important', // Force charcoal background
                },
                backgroundColor: '#2D3436 !important', // Charcoal background - override theme
                '& .MuiAutocomplete-option': {
                  fontSize: '13px',
                  minHeight: '32px',
                  color: '#FFF5E6', // Warm cream text
                  '&:hover': {
                    backgroundColor: 'rgba(32, 178, 170, 0.15)', // Light Sea Green
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(32, 178, 170, 0.25)', // Light Sea Green
                    '&:hover': {
                      backgroundColor: 'rgba(32, 178, 170, 0.35)', // Light Sea Green
                    }
                  }
                }
              }
            }}
          />
        </FormControl>
      ) : (
        <FormControl fullWidth size='small' sx={{ mb: 2 }}>
          <Select
            id="filter-source-select"
            value={selectedState}
            displayEmpty
            renderValue={(value) => {
              if (!value) return renderMutedPlaceholder('Source');
              if (value === 'ALL') return 'ALL SOURCES';
              return value;
            }}
            onChange={(e) => setSelectedState(e.target.value)}
            size='small'
            IconComponent={KeyboardArrowDown}
            inputProps={{
              id: 'filter-source-input',
              name: 'source',
              'aria-label': 'Source'
            }}
            sx={{
              fontSize: '13px',
              height: '36px',
              color: '#FFF5E6',
              backgroundColor: 'rgba(255, 255, 255, 0.07)',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(184, 216, 208, 0.3)',
                borderWidth: '1px',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#20B2AA',
                borderWidth: '1.5px',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#20B2AA',
                borderWidth: '1.5px',
                boxShadow: '0 0 0 2px rgba(184, 216, 208, 0.2)',
              },
              '& .MuiSelect-select': {
                paddingTop: '6px',
                paddingBottom: '6px'
              },
              '& .MuiSvgIcon-root': {
                color: '#20B2AA',
              }
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  '&.MuiPaper-root.MuiMenu-paper': {
                    backgroundColor: '#2D3436 !important', // Force charcoal background  
                  },
                  backgroundColor: '#2D3436 !important', // Charcoal background - override theme
                  '& .MuiMenuItem-root': {
                    fontSize: '13px',
                    minHeight: '32px',
                    color: '#FFF5E6',
                    '&:hover': {
                      backgroundColor: 'rgba(32, 178, 170, 0.15)', // Light Sea Green
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(32, 178, 170, 0.25)', // Light Sea Green
                      '&:hover': {
                        backgroundColor: 'rgba(32, 178, 170, 0.35)', // Light Sea Green
                      }
                    }
                  }
                }
              }
            }}
          >
            <MenuItem value="" sx={{ fontSize: '13px', color: '#FFF5E6' }}>
              {renderMutedPlaceholder('--Select--')}
            </MenuItem>
            <MenuItem value="ALL" sx={{ fontSize: '13px', fontWeight: 600, color: '#FFF5E6' }}>
              ALL SOURCES
            </MenuItem>
            {states.map(st => (
              <MenuItem key={st} value={st} sx={{ fontSize: '13px', color: '#FFF5E6' }}>{st}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {isStationsTab ? (
        <FormControl fullWidth size='small' sx={{ mb: 2 }}>
          <Autocomplete
            multiple
            id="year-multi-select"
            options={['--Select--', 'ALL YEARS', ...years.map(yr => String(yr))]}
            value={Array.isArray(selectedYear) ? selectedYear : (selectedYear ? [selectedYear] : [])}
            open={yearDropdownOpen}
            onOpen={() => setYearDropdownOpen(true)}
            onClose={() => setYearDropdownOpen(false)}
            popupIcon={
              <KeyboardArrowDown
                onClick={(e) => {
                  e.stopPropagation();
                  setYearDropdownOpen(!yearDropdownOpen);
                }}
                sx={{ cursor: 'pointer', fontSize: '20px' }}
              />
            }
            disableCloseOnSelect={false}
            blurOnSelect="mouse"
            onChange={(e, newValue) => {
              // If selecting "--Select--", ignore it
              if (newValue.includes('--Select--')) {
                const filteredValue = newValue.filter(val => val !== '--Select--');
                setSelectedYear(filteredValue);
                return;
              }
              
              // If selecting "ALL YEARS", clear other selections
              if (newValue.includes('ALL YEARS') && newValue.length > 1) {
                setSelectedYear(['ALL YEARS']);
              } else if (newValue.includes('ALL YEARS') && newValue.length === 1) {
                setSelectedYear(newValue);
              } else {
                // If selecting a year and "ALL YEARS" is already selected, remove "ALL YEARS"
                const filteredValue = newValue.filter(val => val !== 'ALL YEARS');
                setSelectedYear(filteredValue);
              }
            }}
            renderInput={(params) => (
              <TextField 
                {...params} 
                placeholder={selectedYear?.length ? '' : 'Year'}
                size="small" 
                inputProps={{
                  ...params.inputProps,
                  'aria-label': 'Year'
                }}
                sx={{
                  fontSize: '13px',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.07)',
                    '& fieldset': {
                      borderColor: 'rgba(184, 216, 208, 0.3)',
                      borderWidth: '1px',
                    },
                    '&:hover fieldset': {
                      borderColor: '#20B2AA',
                      borderWidth: '1.5px',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#20B2AA',
                      borderWidth: '1.5px',
                      boxShadow: '0 0 0 2px rgba(184, 216, 208, 0.2)',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '13px',
                    color: '#FFF5E6', // Warm cream labels
                  },
                  '& .MuiInputBase-input': {
                    color: '#FFF5E6', // Warm cream text
                  }
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option}
                  {...getTagProps({ index })}
                  size="small"
                  sx={{
                    backgroundColor: option === 'ALL YEARS' ? 'rgba(46, 90, 136, 0.15)' : 'rgba(46, 90, 136, 0.08)',
                    fontSize: '13px',
                    fontWeight: option === 'ALL YEARS' ? 600 : 400,
                    height: '24px'
                  }}
                />
              ))
            }
            renderOption={(props, option) => (
              <li {...props} style={{ 
                fontWeight: option === 'ALL YEARS' ? 600 : 400,
                fontSize: '13px'
              }}>
                {option === '--Select--' ? renderMutedPlaceholder('--Select--') : option}
              </li>
            )}
            size='small'
            sx={{
              fontSize: '13px',
              '& .MuiAutocomplete-inputRoot': {
                minHeight: '36px'
              },
              '& .MuiAutocomplete-input': {
                fontSize: '13px',
                height: '1.4375em'
              },
              '& .MuiAutocomplete-endAdornment': {
                '& .MuiSvgIcon-root': {
                  color: '#20B2AA'
                }
              }
            }}
            ListboxProps={{
              style: {
                fontSize: '13px'
              }
            }}
            PaperProps={{
              sx: {
                '&.MuiPaper-root': {
                  backgroundColor: '#2D3436 !important', // Force charcoal background
                },
                backgroundColor: '#2D3436 !important', // Charcoal background - override theme
                '& .MuiAutocomplete-option': {
                  fontSize: '13px',
                  minHeight: '32px',
                  color: '#FFF5E6', // Warm cream text
                  '&:hover': {
                    backgroundColor: 'rgba(32, 178, 170, 0.15)', // Light Sea Green
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(32, 178, 170, 0.25)', // Light Sea Green
                    '&:hover': {
                      backgroundColor: 'rgba(32, 178, 170, 0.35)', // Light Sea Green
                    }
                  }
                }
              }
            }}
          />
        </FormControl>
      ) : (
        <FormControl fullWidth size='small' sx={{ mb: 2 }}>
          <Select
            id="filter-year-select"
            value={selectedYear}
            displayEmpty
            renderValue={(value) => {
              if (!value) return renderMutedPlaceholder('Year');
              if (value === 'ALL') return 'ALL YEARS';
              return value;
            }}
            onChange={(e) => setSelectedYear(e.target.value)}
            size='small'
            IconComponent={KeyboardArrowDown}
            inputProps={{
              id: 'filter-year-input',
              name: 'year',
              'aria-label': 'Year'
            }}
            sx={{
              fontSize: '13px',
              height: '36px',
              color: '#FFF5E6',
              backgroundColor: 'rgba(255, 255, 255, 0.07)',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(184, 216, 208, 0.3)',
                borderWidth: '1px',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#20B2AA',
                borderWidth: '1.5px',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#20B2AA',
                borderWidth: '1.5px',
                boxShadow: '0 0 0 2px rgba(184, 216, 208, 0.2)',
              },
              '& .MuiSelect-select': {
                paddingTop: '6px',
                paddingBottom: '6px'
              },
              '& .MuiSvgIcon-root': {
                color: '#20B2AA',
              }
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  '&.MuiPaper-root.MuiMenu-paper': {
                    backgroundColor: '#2D3436 !important', // Force charcoal background  
                  },
                  backgroundColor: '#2D3436 !important', // Charcoal background - override theme
                  '& .MuiMenuItem-root': {
                    fontSize: '13px',
                    minHeight: '32px',
                    color: '#FFF5E6',
                    '&:hover': {
                      backgroundColor: 'rgba(32, 178, 170, 0.15)', // Light Sea Green
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(32, 178, 170, 0.25)', // Light Sea Green
                      '&:hover': {
                        backgroundColor: 'rgba(32, 178, 170, 0.35)', // Light Sea Green
                      }
                    }
                  }
                }
              }
            }}
          >
            <MenuItem value="" sx={{ fontSize: '13px', color: '#FFF5E6' }}>
              {renderMutedPlaceholder('--Select--')}
            </MenuItem>
            <MenuItem value="ALL" sx={{ fontSize: '13px', fontWeight: 600, color: '#FFF5E6' }}>
              ALL YEARS
            </MenuItem>
            {years.map(yr => (
              <MenuItem key={yr} value={yr} sx={{ fontSize: '13px', color: '#FFF5E6' }}>{yr}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Season dropdown for survey tabs with seasonal filters */}
      {(isTrawlStationsTab || isPlanktonStationsTab) && (
        <FormControl fullWidth size='small' sx={{ mb: 2 }}>
          <Select
            id="filter-season-select"
            value={selectedSeason}
            displayEmpty
            renderValue={(value) => {
              if (!value) return renderMutedPlaceholder('Season');
              if (value === 'ALL') return 'ALL SEASONS';
              return value;
            }}
            onChange={(e) => setSelectedSeason(e.target.value)}
            size='small'
            IconComponent={KeyboardArrowDown}
            inputProps={{
              id: 'filter-season-input',
              name: 'season',
              'aria-label': 'Season'
            }}
            sx={{
              fontSize: '13px',
              height: '36px',
              color: '#FFF5E6',
              backgroundColor: 'rgba(255, 255, 255, 0.07)',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(184, 216, 208, 0.3)',
                borderWidth: '1px',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#20B2AA',
                borderWidth: '1.5px',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#20B2AA',
                borderWidth: '1.5px',
                boxShadow: '0 0 0 2px rgba(184, 216, 208, 0.2)',
              },
              '& .MuiSelect-select': {
                paddingTop: '6px',
                paddingBottom: '6px'
              },
              '& .MuiSvgIcon-root': {
                color: '#20B2AA',
              }
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  '&.MuiPaper-root.MuiMenu-paper': {
                    backgroundColor: '#2D3436 !important', // Force charcoal background  
                  },
                  backgroundColor: '#2D3436 !important', // Charcoal background - override theme
                  '& .MuiMenuItem-root': {
                    fontSize: '13px',
                    minHeight: '32px',
                    color: '#FFF5E6',
                    '&:hover': {
                      backgroundColor: 'rgba(32, 178, 170, 0.15)', // Light Sea Green
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(32, 178, 170, 0.25)', // Light Sea Green
                      '&:hover': {
                        backgroundColor: 'rgba(32, 178, 170, 0.35)', // Light Sea Green
                      }
                    }
                  }
                }
              }
            }}
          >
            <MenuItem value="" sx={{ fontSize: '13px', color: '#FFF5E6' }}>
              {renderMutedPlaceholder('--Select--')}
            </MenuItem>
            <MenuItem value="ALL" sx={{ fontSize: '13px', fontWeight: 600, color: '#FFF5E6' }}>
              ALL SEASONS
            </MenuItem>
            {isPlanktonStationsTab && (
              <MenuItem value="Spring" sx={{ fontSize: '13px', color: '#FFF5E6' }}>Spring</MenuItem>
            )}
            {!isPlanktonStationsTab && (
              <MenuItem value="Summer" sx={{ fontSize: '13px', color: '#FFF5E6' }}>Summer</MenuItem>
            )}
            <MenuItem value="Fall" sx={{ fontSize: '13px', color: '#FFF5E6' }}>Fall</MenuItem>
          </Select>
        </FormControl>
      )}

      <DateRangeFields
        dateRangeStart={dateRangeStart}
        setDateRangeStart={setDateRangeStart}
        dateRangeEnd={dateRangeEnd}
        setDateRangeEnd={setDateRangeEnd}
      />

      <Button 
        fullWidth
        variant="contained" 
        onClick={handleFilter}
        sx={{
          background: '#20B2AA', // Light Sea Green button
          color: '#FFF5E6', // Warm cream text
          textTransform: 'none',
          fontWeight: 600,
          padding: '8px 0',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s ease',
          '&:hover': {
            background: '#1A9B99', // Darker Light Sea Green on hover
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            transform: 'translateY(-1px)'
          }
        }}
      >
        Apply Filters
      </Button>

      {/* Show trawl shapefile toggle for trawl tabs only */}
      {isTrawlStationsTab && (
        <>
          <Box sx={{ mt: 2 }} />
          <FormControlLabel
            control={
              <Switch 
                checked={showTrawlShapefile}
                onChange={(e) => setShowTrawlShapefile(e.target.checked)}
                size="small"
                sx={commonSwitchStyles}
              />
            }
            label={
              <Typography sx={{ fontSize: '13px', color: '#FFF5E6' }}>
                Show Current Trawl Zones
              </Typography>
            }
            sx={{ 
              margin: 0,
              width: '100%',
              justifyContent: 'space-between'
            }}
            labelPlacement="start"
          />
          
          {/* Historic Trawl Zones toggle */}
          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Switch 
                  checked={showHistoricTrawlShapefile}
                  onChange={(e) => setShowHistoricTrawlShapefile(e.target.checked)}
                  size="small"
                  sx={commonSwitchStyles}
                />
              }
              label={
                <Typography sx={{ fontSize: '13px', color: '#FFF5E6' }}>
                  Show Historic Trawl Zones
                </Typography>
              }
              sx={{ 
                margin: 0,
                width: '100%',
                justifyContent: 'space-between'
              }}
              labelPlacement="start"
            />
          </Box>
        </>
      )}
      
      {/* Show longline shapefile toggles for longline tabs only */}
      {activeTab.includes('Longline') && (
        <>
          <Box sx={{ mt: 2 }} />

          {/* 2008 Longline Zones toggle */}
          <FormControlLabel
            control={
              <Switch 
                checked={showLongline2008Shapefile}
                onChange={(e) => setShowLongline2008Shapefile(e.target.checked)}
                size="small"
                sx={commonSwitchStyles}
              />
            }
            label={
              <Typography sx={{ fontSize: '13px', color: '#FFF5E6' }}>
                Show 2008 Longline Zones
              </Typography>
            }
            sx={{ 
              margin: 0,
              width: '100%',
              justifyContent: 'space-between'
            }}
            labelPlacement="start"
          />
          
          {/* 2015 Longline Zones toggle */}
          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Switch 
                  checked={showLongline2015Shapefile}
                  onChange={(e) => setShowLongline2015Shapefile(e.target.checked)}
                  size="small"
                  sx={commonSwitchStyles}
                />
              }
              label={
                <Typography sx={{ fontSize: '13px', color: '#FFF5E6' }}>
                  Show 2015 Longline Zones
                </Typography>
              }
              sx={{ 
                margin: 0,
                width: '100%',
                justifyContent: 'space-between'
              }}
              labelPlacement="start"
            />
          </Box>
          
          {/* 2024 Longline Zones toggle */}
          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Switch 
                  checked={showLongline2024Shapefile}
                  onChange={(e) => setShowLongline2024Shapefile(e.target.checked)}
                  size="small"
                  sx={commonSwitchStyles}
                />
              }
              label={
                <Typography sx={{ fontSize: '13px', color: '#FFF5E6' }}>
                  Show 2024 Longline Zones
                </Typography>
              }
              sx={{ 
                margin: 0,
                width: '100%',
                justifyContent: 'space-between'
              }}
              labelPlacement="start"
            />
          </Box>
        </>
      )}
    </Paper>
  )
}

export default FilterOverlay
